import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { ScanLine, RefreshCw, Copy, ArrowRight, ShieldCheck, AlertTriangle } from "lucide-react";
import { ToolLayout } from "@/components/layout/ToolLayout";
import { TableScroll } from "@/components/layout/ContentBlock";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { FaqSection } from "@/components/seo/FaqSection";
import { toast } from "sonner";
import { ToolSeoSections } from "@/components/seo/ToolSeoSections";
import { pageHead, softwareAppJsonLd, breadcrumbJsonLd, ldScript } from "@/lib/seo";
import { detectBarcode, type Confidence, type DetectionResult } from "@/lib/detect";
import { usePrefillValue } from "@/lib/usePrefillValue";

const PATH = "/barcode-type-detector";
const TITLE = "Barcode Type Detector — Identify UPC, EAN, GTIN & ISBN";
const DESC =
  "Free barcode type detector. Paste any value and instantly identify whether it's UPC-A, UPC-E, EAN-8, EAN-13, GTIN-14, ISBN-10/13 or a QR payload.";

const EXAMPLES: { value: string; label: string }[] = [
  { value: "036000291452", label: "UPC-A" },
  { value: "01234565", label: "UPC-E" },
  { value: "73513537", label: "EAN-8" },
  { value: "5012345678900", label: "EAN-13" },
  { value: "9780131103627", label: "ISBN-13" },
  { value: "0131103628", label: "ISBN-10" },
  { value: "10614141000415", label: "GTIN-14" },
  { value: "https://readbarcode.com", label: "QR / URL" },
];

const FAQ = [
  {
    q: "How does the barcode type detector decide what format I pasted?",
    a: "It cleans whitespace and hyphens from your input, checks the length, then runs the right check-digit algorithm for each candidate — GS1 mod-10 for GTIN-8/12/13/14 and mod-11 for ISBN-10. A matching check digit means high confidence; a length-only match means lower confidence.",
  },
  {
    q: "What's the difference between UPC and EAN?",
    a: "UPC-A is 12 digits, used mainly in the US and Canada. EAN-13 is 13 digits, used worldwide. Any UPC-A can be expressed as an EAN-13 by prefixing a zero — the detector flags both possibilities when relevant.",
  },
  {
    q: "How Do I Tell If a 13-Digit Number Is an ISBN-13 or an EAN-13?",
    a: "ISBN-13s always start with the 978 or 979 GS1 prefix (Bookland). The detector calls these out as ISBN-13 candidates and notes that they're simultaneously valid EAN-13s.",
  },
  {
    q: "Does It Work for QR Code Payloads?",
    a: "Yes — if your input contains non-digit characters or a URL scheme, the detector flags it as a QR / Text payload and points you to the QR Code Reader.",
  },
  {
    q: "Can It Detect a GTIN-14 From Just the Number?",
    a: "Yes. Any 14-digit numeric value with a valid GS1 check digit is treated as a GTIN-14 / ITF-14 — the carton-level identifier used in logistics.",
  },
  {
    q: "What If No Format Matches?",
    a: "If the length isn't a standard barcode length and there's no obvious text payload, the detector returns Unknown with a reason. Try the Online Barcode Reader to scan the source image instead of typing.",
  },
  {
    q: "Can I Find a Product by Its Barcode?",
    a: "Not on this page: the detector names the format and checks the digits, but it has no product database. Open a UPC or EAN result in UPC Lookup or EAN Lookup, which validate the code and link out to a Google search and a third-party barcode database for that number. Codes starting with 2 and internal SKUs rarely appear in any public catalog.",
  },
  {
    q: "What Is a Barcode Identifier?",
    a: "It is a tool that names the format of a barcode value, such as UPC-A, EAN-13 or ISBN-13, from its length, prefix and check digit. The term is also used for the number itself, for example the GTIN a brand assigns to a product. This page is the first kind, and the GTIN Validator or ISBN Barcode Reader then checks the number in detail.",
  },
];

const CRUMBS = [
  { name: "Home", path: "/" },
  { name: "Lookup & Validators", path: "/lookup-validators" },
  { name: "Barcode Type Detector", path: PATH },
];

export const Route = createFileRoute("/barcode-type-detector")({
  component: BarcodeTypeDetectorPage,
  head: () => {
    const base = pageHead({ title: TITLE, description: DESC, path: PATH });
    return {
      ...base,
      scripts: [
        ldScript(
          softwareAppJsonLd({
            name: "ReadBarcode Barcode Type Detector",
            description: DESC,
            path: PATH,
          }),
        ),
        ldScript(breadcrumbJsonLd(CRUMBS)),
      ],
    };
  },
});

function BarcodeTypeDetectorPage() {
  return (
    <ToolLayout
      eyebrow="Online Tool"
      title="Barcode Type Detector"
      description="Paste any barcode value and we'll tell you whether it's UPC, EAN, GTIN, ISBN or a QR Code payload — with confidence and a recommended next tool. It strips spaces, hyphens, dots and underscores, then checks the GS1 mod-10 or ISBN-10 mod-11 check digit of each UPC-A, EAN, GTIN-14 and ISBN candidate."
      crumbs={CRUMBS}
      content={
        <>
          <SupportedFormats />
          <ConfidenceLegend />
          <ToolSeoSections
            aboutHeading="About this barcode type detector"
            about={
              <>
                <p>
                  This barcode type detector takes a raw barcode value — typed, pasted from a scanner,
                  or copied from a spreadsheet — and identifies the most likely format. It runs the
                  correct check-digit algorithm for each candidate (GS1 mod-10 for UPC / EAN / GTIN,
                  mod-11 for ISBN-10) and reports confidence so you can tell a confirmed match from a
                  length-only guess.
                </p>
                <p>
                  Use it to settle UPC vs EAN questions, decide whether a 13-digit number is an ISBN-13,
                  or pick the right validator for an unfamiliar code.
                </p>
              </>
            }
            whenToUse={[
              "Identifying an unknown number pulled from an invoice or product feed.",
              "Deciding whether a 13-digit code is an EAN-13 or an ISBN-13 before lookup.",
              "Telling apart a UPC-A and a compressed UPC-E from a shelf-edge label.",
              "Routing a scanned value to the right validator instead of guessing.",
              "Detecting that a copied string is actually a QR Code URL, not a numeric barcode.",
            ]}
            howItWorks={[
              {
                title: "Paste the value",
                desc: "Type or paste a barcode. Spaces, hyphens, dots and underscores are stripped automatically.",
              },
              {
                title: "Length match",
                desc: "We line up the cleaned value against every standard barcode length (6, 7, 8, 10, 12, 13, 14).",
              },
              {
                title: "Check digit",
                desc: "Each candidate is verified with the right algorithm — GS1 mod-10 for GTINs, mod-11 for ISBN-10.",
              },
              {
                title: "Confidence score",
                desc: "Length + valid check digit → high. Length only → low. URL / text payload → medium.",
              },
              {
                title: "Next-tool suggestion",
                desc: "Every candidate links straight to the matching ReadBarcode tool — UPC Lookup, EAN Lookup, GTIN Validator, ISBN Reader or QR Reader.",
              },
            ]}
            whatToAvoid={[
              "Pasting a multi-line list — the detector evaluates a single value at a time.",
              "Treating a low-confidence match as confirmed; the value may just happen to have the right length.",
              "Relying on detection alone for ISBN-10 codes — booksellers normally re-issue them as ISBN-13.",
              "Forgetting that 12-digit UPC-A and 13-digit EAN-13 ‘0+UPC' encode the same GTIN.",
              "Mistaking an internal SKU that happens to be 12 digits for a real UPC — the check digit usually fails.",
            ]}
            tips={[
              "Use the example chips below to see how high-confidence matches are reported.",
              "If you see two ‘high' candidates (EAN-13 and ISBN-13 for a 978-prefixed value), both are correct — pick the lookup that fits your goal.",
              "Suspect a GTIN type? Run the cleaned value through the GTIN Validator to confirm.",
              "When the result is Unknown, double-check you didn't paste extra characters from a spreadsheet (apostrophes, BOM).",
              "For UPC-E payloads, expand to UPC-A in the UPC Lookup tool before doing any product search.",
            ]}
          />
          <h2>What Type of Barcode Is This?</h2>
          <p>Paste the digits printed under the bars into the detector above and it lists every format they fit, each with a confidence level. A few patterns come up often when people check barcode type:</p>
          <ul>
            <li>14 digits starting with 0 are a shorter GTIN padded with leading zeros, which leave the check digit unchanged.</li>
            <li>13 digits starting with 2 are a GS1 restricted circulation number for use inside one company or region, so public lookups rarely list them.</li>
            <li>Numbers in parentheses, such as (01) followed by 14 digits, form a GS1 element string; paste only the 14 digits after (01) to test that GTIN.</li>
            <li>Letters mixed with digits are reported as QR / Text. That only rules out a GTIN or ISBN, since 1D symbologies such as Code 128 and Code 39 carry letters too.</li>
          </ul>
          <h2>Barcode Identifier Online for Numbers</h2>
          <p>This barcode identifier is free, works on typed or pasted numbers, and runs in your browser with no account.</p>
          <p>It suits barcode identification from text, such as an invoice line or a product feed, one value at a time. The barcode type identifier reads the number, not the bars, so it cannot tell a UPC-A symbol from a Code 128 symbol carrying the same 12 digits. The <Link to="/barcode-guides/barcode-formats">guide to common barcode formats</Link> explains where each one is used.</p>
          <h3>Identify a Barcode From an Image</h3>
          <p>The detector does not accept photos. To identify the barcode type from a picture, use <Link to="/read-barcode-from-image">read a barcode from an image</Link>, which names the symbology it decodes, such as EAN-13 or Code 128. Paste the decoded value here to test its check digit.</p>
          <FaqSection items={FAQ} />
        </>
      }
      related={<RelatedTools />}
    >
      <DetectorApp />
    </ToolLayout>
  );
}

function DetectorApp() {
  const [value, setValue] = useState("");
  usePrefillValue((v) => setValue(v));
  const result = useMemo(() => detectBarcode(value), [value]);

  return (
    <div className="grid gap-6 lg:grid-cols-[1.1fr_1fr]">
      <Card>
        <CardContent className="p-5 md:p-6">
          <div className="flex flex-col gap-3">
            <label htmlFor="btd-input" className="text-sm font-medium">
              Barcode value
            </label>
            <Input
              id="btd-input"
              autoComplete="off"
              spellCheck={false}
              placeholder="e.g. 5012345678900 or https://example.com"
              maxLength={256}
              value={value}
              onChange={(e) => setValue(e.target.value)}
              aria-invalid={result.state === "empty"}
            />
            <p className="text-xs text-muted-foreground">
              Spaces, hyphens, dots and underscores are ignored. Detection runs entirely in your
              browser.
            </p>
            <div className="flex flex-wrap gap-2 pt-1 text-xs">
              {EXAMPLES.map((ex) => (
                <button
                  key={ex.value}
                  type="button"
                  onClick={() => setValue(ex.value)}
                  className="rounded-md border border-border bg-background px-2 py-1 font-mono text-xs text-muted-foreground hover:border-primary/40 hover:text-foreground"
                  aria-label={`${ex.label} example`}
                  title={ex.label}
                >
                  {ex.value}
                </button>
              ))}
            </div>
            <div>
              <Button type="button" size="sm" variant="ghost" onClick={() => setValue("")}>
                <RefreshCw className="mr-2 h-3.5 w-3.5" /> Clear
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <ResultPanel result={result} />
    </div>
  );
}

function ResultPanel({ result }: { result: DetectionResult }) {
  const copy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Copied to clipboard");
    } catch {
      toast.error("Couldn't access clipboard");
    }
  };

  return (
    <Card>
      <CardContent className="flex h-full flex-col p-5 md:p-6">
        <p className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
          Detection
        </p>

        {result.state === "idle" && (
          <div className="mt-4 flex flex-1 flex-col items-center justify-center gap-3 rounded-lg border border-dashed border-border bg-muted/30 p-6 text-center">
            <ScanLine className="h-7 w-7 text-muted-foreground" aria-hidden />
            <p className="max-w-sm text-sm text-muted-foreground">
              Paste a barcode value to identify the format. Each match comes with a confidence level
              and a direct link to the right validator.
            </p>
          </div>
        )}

        {result.state === "empty" && (
          <p className="mt-4 rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">
            Nothing to detect — your input only contained spaces or punctuation.
          </p>
        )}

        {result.state === "ok" && (
          <div className="mt-4 flex flex-col gap-4">
            <div className="flex items-center justify-between gap-3 rounded-md border border-border bg-background p-3">
              <div className="min-w-0">
                <div className="text-xs uppercase tracking-wide text-muted-foreground">
                  Cleaned value
                </div>
                <div className="mt-0.5 break-all font-mono text-sm">{result.cleaned}</div>
                <div className="text-xs text-muted-foreground">
                  {result.cleaned.length} characters
                </div>
              </div>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => copy(result.cleaned)}
                aria-label="Copy cleaned value"
              >
                <Copy className="h-3.5 w-3.5" />
              </Button>
            </div>

            {result.candidates.map((c, i) => (
              <div
                key={`${c.format}-${i}`}
                className="rounded-lg border border-border bg-background p-4"
              >
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm font-semibold">{c.format}</span>
                  <ConfidenceBadge level={c.confidence} />
                </div>
                <p className="mt-2 text-sm text-muted-foreground">{c.reason}</p>
                {c.nextTool && (
                  <div className="mt-3">
                    <a
                      href={c.nextTool.path}
                      className="inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline"
                    >
                      Open in {c.nextTool.label}
                      <ArrowRight className="h-3.5 w-3.5" />
                    </a>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function ConfidenceBadge({ level }: { level: Confidence }) {
  if (level === "high") {
    return (
      <Badge
        className="border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300"
        variant="outline"
      >
        <ShieldCheck className="mr-1 h-3 w-3" /> High confidence
      </Badge>
    );
  }
  if (level === "medium") {
    return (
      <Badge variant="outline" className="text-xs">
        Medium confidence
      </Badge>
    );
  }
  return (
    <Badge variant="outline" className="border-destructive/40 text-destructive">
      <AlertTriangle className="mr-1 h-3 w-3" /> Low confidence
    </Badge>
  );
}

function SupportedFormats() {
  const rows = [
    { f: "UPC-A", len: "12 digits", note: "US/Canada retail. GTIN-12." },
    { f: "UPC-E", len: "6–8 digits", note: "Compressed UPC for small packages." },
    { f: "EAN-8", len: "8 digits", note: "Short EAN. GTIN-8." },
    { f: "EAN-13", len: "13 digits", note: "Global retail standard. GTIN-13." },
    { f: "GTIN-14", len: "14 digits", note: "ITF-14 cartons and cases." },
    { f: "ISBN-10", len: "10 chars", note: "Legacy book identifier (mod-11)." },
    { f: "ISBN-13", len: "13 digits", note: "978/979 prefix — modern book ID." },
    { f: "QR / Text", len: "Variable", note: "URLs and free-text payloads." },
  ];
  return (
    <>
      <h2>Supported Formats</h2>
      <p>Every format the detector can identify, with the rules used to spot it.</p>
      <TableScroll>
        <table>
          <thead>
            <tr>
              <th scope="col">Format</th>
              <th scope="col">Length</th>
              <th scope="col">Notes</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.f}>
                <td>
                  <strong>{r.f}</strong>
                </td>
                <td>{r.len}</td>
                <td>{r.note}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </TableScroll>
    </>
  );
}

const CONFIDENCE_LABEL: Record<Confidence, string> = {
  high: "High confidence",
  medium: "Medium confidence",
  low: "Low confidence",
};

function ConfidenceLegend() {
  const items: { level: Confidence; text: string }[] = [
    {
      level: "high",
      text: "Length matches and the check digit verifies — the format is confirmed.",
    },
    {
      level: "medium",
      text: "Length or structural shape matches but there's no check digit to verify (e.g. UPC-E, QR text).",
    },
    {
      level: "low",
      text: "Length matches a known format, but the check digit fails. Probably a typo or a different format altogether.",
    },
  ];
  return (
    <>
      <h2>How Confidence Is Scored</h2>
      <p>Each candidate gets one of three confidence levels, set by whether its length matches and whether a check digit could be verified.</p>
      <ul>
        {items.map((i) => (
          <li key={i.level}>
            <strong>{CONFIDENCE_LABEL[i.level]}</strong>
            {` ${i.text}`}
          </li>
        ))}
      </ul>
    </>
  );
}
function RelatedTools() {
  const related = [
    {
      path: "/upc-lookup",
      label: "UPC Lookup",
      desc: "Validate UPC-A / UPC-E and decode the number system digit.",
    },
    {
      path: "/ean-lookup",
      label: "EAN Lookup",
      desc: "Validate EAN-13 / EAN-8 and decode the GS1 country prefix.",
    },
    {
      path: "/gtin-validator",
      label: "GTIN Validator",
      desc: "Verify the GS1 check digit on any GTIN-8/12/13/14.",
    },
    {
      path: "/check-digit-calculator",
      label: "Check Digit Calculator",
      desc: "Compute the missing check digit from the body.",
    },
    {
      path: "/barcode-reader",
      label: "Online Barcode Reader",
      desc: "Decode straight from an image or your camera.",
    },
    {
      path: "/qr-code-reader",
      label: "QR Code Reader",
      desc: "Decode QR Code payloads (URL, text, Wi-Fi, contact).",
    },
    {
      path: "/read-barcode-from-image",
      label: "Read Barcode From Image",
      desc: "Decode a photo or screenshot and see which symbology it uses.",
    },
    {
      path: "/barcode-guides/barcode-formats",
      label: "Barcode Formats Guide",
      desc: "Where UPC, EAN, Code 128 and other formats are used.",
    },
  ];
  return (
    <section className="mt-12">
      <h2 className="text-2xl font-semibold tracking-tight">Related Validators &amp; Tools</h2>
      <div className="mt-4 grid gap-3 sm:grid-cols-2">
        {related.map((r) => (
          <a
            key={r.path}
            href={r.path}
            className="group rounded-lg border border-border bg-background p-4 transition-colors hover:border-primary/40"
          >
            <div className="text-sm font-semibold group-hover:text-primary">{r.label}</div>
            <div className="mt-1 text-sm text-muted-foreground">{r.desc}</div>
          </a>
        ))}
      </div>
    </section>
  );
}
